'use client';

import { notifications } from '@mantine/notifications';
import { setCookie } from 'cookies-next';
import {
  AuthError,
  ConfirmationResult,
  onIdTokenChanged,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
  User as AuthUser,
} from 'firebase/auth';
import { onSnapshot } from 'firebase/firestore';
import localforage from 'localforage';
import { useCallback, useEffect, useRef, useState } from 'react';

import { importFirebaseMessaging } from '@/hooks/use-firebase-messaging';
import useRollbar from '@/hooks/use-rollbar';
import { perform, useSafeTransition } from '@/hooks/use-safe-transition';
import { useSyncRouter } from '@/hooks/use-sync-router';
import { ActivityLogAction } from '@/typings/activity-log';
import { User } from '@/typings/user';
import { ADMIN_DASHBOARD_ROUTE, LOGIN_ROUTE } from '@/utils/constants';
import {
  checkAndCreateUserOnFirestore,
  clearUserToken,
  firebaseAuth,
  firebaseMessaging,
  getDocument,
  logUserActivity,
  refreshUserToken,
} from '@/utils/firebase';
import { getMessage, getUserMessage, UserOnlyErrorCodes } from '@/utils/firebase-auth';
import { USER_TOKEN } from '@/utils/storage-keys';

import { signUpAction } from '../actions/sign-up-action';

import type { Unsubscribe } from 'firebase/messaging';

export const FIRESTORE_USER = 'firestoreUser';

export type RegisterData = {
  email: string;
  password: string;
  uid?: string;
} & Partial<User>;

export type FirebasePhoneAuth = {
  handlePhoneLogin: (phoneNumber: string) => Promise<void>;
  handleValidateCode: (code: string) => Promise<void>;
  phoneNumber: string | null;
  confirmation: ConfirmationResult | null;
};

export type UseFirebaseAuth = {
  user: User | null;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, uid?: string) => Promise<void>;
  logout: (doNotEraseFromLocalStorage: string[]) => Promise<void>;
  resetError: () => void;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
  phoneAuth: FirebasePhoneAuth;
};

export function useFirebaseAuth(initialUser?: User | null): UseFirebaseAuth {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [firestoreUser, setFirestoreUser] = useState<User | null>(initialUser || null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);

  const rollbar = useRollbar();
  const router = useSyncRouter();
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const firstTimeLoadingUser = useRef(true);
  const [lodingSignUp, startSignUpTransition, signUpError] = useSafeTransition('Sign Up');

  // ----- Hooks ------
  useEffect(() => {
    if (!user && !firstTimeLoadingUser.current) setFirestoreUser(null);
    if (!user) return;

    firstTimeLoadingUser.current = false;

    const userId = user.uid;
    const docRef = getDocument('users', userId);
    return onSnapshot(
      docRef,
      { includeMetadataChanges: true },
      (docSnapshot) => {
        if (!docSnapshot.exists()) {
          rollbar.critical(
            'Firestore user document does not exist. This is likely a Firebase persistence issue on the client side.',
            {
              userId,
              docSnapshot: docSnapshot,
            },
          );
          notifications.show({
            title: 'Erro de autenticação',
            message:
              'Não foi possível carregar os dados do usuário. Por favor, limpe o cache do navegador e faça login novamente.',
            color: 'red',
          });
          return;
        }

        const user = { ...(docSnapshot.data() as User), id: docSnapshot.id };
        setFirestoreUser({
          ...user,
          authId: user.authId,
        });
      },
      (error: Error) => {
        rollbar.error('useFirebaseAuth - Error on get firestore user', error);
        notifications.show({
          title: 'Erro de autenticação',
          message:
            'Não foi possível carregar os dados do usuário. Por favor, limpe o cache do navegador e faça login novamente.',
          color: 'red',
        });
      },
    );
  }, [rollbar, user]);

  useEffect(() => {
    if (!firebaseAuth) return;
    return onIdTokenChanged(firebaseAuth, async (user) => {
      if (!user) {
        setUser(null);
        setError(null);
        await clearUserToken();
      } else {
        setUser(user);
        setError(null);
        const token = await user.getIdToken();
        await refreshUserToken(token);
      }
    });
  }, []);

  useEffect(() => {
    if (signUpError) {
      setError(signUpError.message);
    }
  }, [signUpError]);

  // ----- Methods ------
  const handleAuthError = useCallback(
    (customMessage: string, err: unknown, options?: { withNotification?: boolean }) => {
      const error = err as AuthError;

      // If we show a notification we don't need to show the error in the form
      if (options?.withNotification)
        notifications.show({
          title: 'Não foi possível completar a ação',
          message: getUserMessage(error),
          color: 'red',
        });
      else {
        setError(getUserMessage(error));
        setLoading(false);
      }

      // If the error is userOnly we don't need to send it to rollbar
      if (UserOnlyErrorCodes.includes(error.code)) return;
      rollbar.error(customMessage, error, getMessage(error));
    },
    [rollbar],
  );

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      setError(null);
      try {
        const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
        const token = await userCredential.user.getIdToken();
        await refreshUserToken(token);
        await logUserActivity(userCredential.user.uid, ActivityLogAction.Login);
        router.push(ADMIN_DASHBOARD_ROUTE);
      } catch (err) {
        handleAuthError('AuthProvider - Falha ao fazer login', err);
      }
      setLoading(false);
    },
    [handleAuthError, router],
  );

  const handleSignUp = useCallback(
    async (email: string, password: string, uid?: string) => {
      startSignUpTransition(async () => {
        await perform(signUpAction, email, password, uid);
        handleLogin(email, password);
      });
    },
    [startSignUpTransition, handleLogin],
  );

  const handlePhoneLogin = useCallback(
    async (phoneNumber: string) => {
      try {
        setLoading(true);
        if (recaptchaRef.current) {
          const recaptcha = document.getElementById('recaptcha-container');
          if (recaptcha) {
            recaptchaRef.current.clear();
            recaptcha.innerHTML = `<div id="recaptcha" style="display: none;"></div>`;
          }
        }
        recaptchaRef.current = new RecaptchaVerifier(
          firebaseAuth,
          // This string below is the ID of the HTML element which will render recaptcha (<div id="recaptcha-container" />)
          // An error will be thrown if no HTML element with this id exists
          'recaptcha',
          {
            size: 'invisible',
          },
        );

        const res = await signInWithPhoneNumber(firebaseAuth, phoneNumber, recaptchaRef.current);
        setPhoneNumber(phoneNumber);
        setConfirmation(res);
        setError(null);
        setLoading(false);
      } catch (error) {
        handleAuthError('Error on signInWithPhoneNumber', error);
      }
    },
    [handleAuthError],
  );

  const handleValidateCode = useCallback(
    async (code: string) => {
      if (!confirmation) {
        rollbar.error('OTP not provided');
        setError('Código inválido');
        return;
      }
      try {
        setLoading(true);
        const authUser = await confirmation.confirm(code);
        if (authUser?.user?.phoneNumber && authUser?.user?.uid) {
          await checkAndCreateUserOnFirestore(authUser);
          const user = firebaseAuth.currentUser;
          if (user) {
            const token = await user.getIdToken();
            const maxAge = 1000 * 60 * 60 * 24 * 365; // 1 year for now
            setCookie(USER_TOKEN, token, { maxAge });
          }
          router.push(ADMIN_DASHBOARD_ROUTE);
        }
        setLoading(false);
        setError(null);
      } catch (error) {
        handleAuthError('Error on validating otp code', error);
      }
    },
    [confirmation, rollbar, router, handleAuthError],
  );

  const handleLogout = useCallback(
    async (doNotEraseFromLocalStorage: string[]) => {
      try {
        await router.push(LOGIN_ROUTE);
        await signOut(firebaseAuth);
        await clearUserToken();
        setUser(null);
        setFirestoreUser(null);
        setError(null);
        Object.keys(localStorage).forEach((key) => {
          if (!doNotEraseFromLocalStorage.includes(key)) localStorage.removeItem(key);
        });
        localforage.removeItem('FCM_TOKEN');
      } catch (err) {
        handleAuthError('AuthProvider - Error on Logout', err, { withNotification: true });
      }
    },
    [handleAuthError, router],
  );

  const handleResetPwd = useCallback(
    async (email: string) => {
      setLoading(true);
      setError(null);
      try {
        await sendPasswordResetEmail(firebaseAuth, email);
      } catch (err) {
        handleAuthError('AuthProvider - Error on reseting password', err);
      }
      setLoading(false);
    },
    [handleAuthError],
  );

  const handleResetError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (!firebaseMessaging) return;

    let unsub: Unsubscribe | null = null;
    importFirebaseMessaging()
      .then((messaging) => {
        if (!messaging) return;
        unsub = messaging.onMessage(firebaseMessaging, ({ messageId, notification }) => {
          notifications.show({
            id: messageId,
            title: notification?.title,
            message: notification?.body,
            autoClose: false,
          });
        });
      })
      .catch((err) =>
        rollbar.error('AuthProvider - Falha ao importar firebase messaging', err as Error),
      );
    if (unsub) return unsub;
  }, [firestoreUser?.id, rollbar]);

  const phoneAuth = {
    handlePhoneLogin,
    handleValidateCode,
    phoneNumber,
    confirmation,
  };

  return {
    user: firestoreUser,
    error,
    login: handleLogin,
    logout: handleLogout,
    signUp: handleSignUp,
    resetError: handleResetError,
    resetPassword: handleResetPwd,
    loading: loading || lodingSignUp,
    phoneAuth,
  };
}
